import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Approval {
  id: string;
  targetKind: string;
  targetId: string;
  requiredSigners: string[];
  currentStep: number;
  status: 'pending' | 'approved' | 'rejected' | 'canceled';
  history: Array<{ user_id: string; step: number; action: 'approved' | 'rejected'; reason?: string; at: string; ip?: string | null }>;
  createdBy: string | null;
  createdAt: string;
}

export function useApprovals(params: { status?: string; mine?: boolean } = {}) {
  return useQuery({
    queryKey: ['approvals', params],
    queryFn: async () => {
      const r = await api.get<{ approvals: Approval[] }>('/api/approvals', { params: { status: params.status, mine: params.mine ? '1' : undefined } });
      return r.data.approvals;
    },
  });
}

export function useCreateApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { targetKind: string; targetId: string; requiredSigners: string[] }) => {
      const r = await api.post<Approval>('/api/approvals', input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, reason }: { id: string; action: 'approved' | 'rejected'; reason?: string }) => {
      const r = await api.post<Approval>(`/api/approvals/${id}/decision`, { action, reason });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}

/** Requester withdraws a still-pending approval (POST /approvals/:id/cancel). */
export function useCancelApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post<Approval>(`/api/approvals/${id}/cancel`, {});
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approvals'] }),
  });
}
